import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectFilteredPeopleMembers } from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import { AccountAccessOptionsFields } from 'src/components/AccountAccessOptions/AccountAccessOptions'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import SelectRoleOnInvite from 'src/components/SelectRoleOnInvite/SelectRoleOnInvite'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userEmailOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import {
  getOwnUser,
  getRecentUsers,
} from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { FormProps } from 'src/types'
import { getReactNodeSourceString } from 'src/utils/reactNode.utils'
import styled, { css } from 'styled-components'

interface UserAndNameInputBoxProps {
  className?: string
  'data-testid'?: string
  displayNameProps?: FormProps<string>
  displayNameFieldLabel?: React.ReactNode
  recipientFirstNameProps?: FormProps<string>
  recipientLastNameProps?: FormProps<string>
  userFieldLabel: React.ReactNode
  selectedUserHasName?: boolean
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  tooltip?: React.ReactElement
  userEmailProps: FormProps<string[]>
  isMulti?: boolean
  suggestedProps?: {
    showSuggested: boolean | undefined
    showOwnUserInSuggested?: boolean
  }
  displayPills?: boolean
  bottomElement?: React.ReactElement
  id?: string
  filterUserbyAssocationTypes?: UserAssociation[]
  getRecipientWarningMessage?: (
    hasSelectedUsersNotInOrg: boolean,
  ) => React.ReactNode
  setSelectedUsersNotInOrg?: (users: string[]) => void
  unableToAddNewUser?: boolean
  showRoles?: boolean
  setRoleIndex?: (index: number | undefined) => void
  setRole?: (role: OrganizationRoles) => void
  setBookkeeperAccess?: (fields: AccountAccessOptionsFields) => void
  errorBorder?: boolean
}

const StyledTextInputField = styled(TextInputField)`
  &:nth-child(n + 2) {
    margin-top: 24px;
  }
  ${getFontCss(
    16,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
`

const UserAndNameInputBox: React.FC<UserAndNameInputBoxProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const userEmailMap = useSelector(selectUserEmailMap)
  const user = useCurrentUser()
  const memberListModels = useSelector(selectFilteredPeopleMembers)

  const memberEmailMap = useMemo(() => {
    return Object.values(memberListModels).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.email]: curr,
      }),
      {},
    )
  }, [memberListModels])

  const emailMap = useMemo(() => {
    return { ...userEmailMap, ...memberEmailMap }
  }, [userEmailMap, memberEmailMap])

  const addDeleteVal = (newSet: Set<string>, val: string): void => {
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
  }

  const selectedOptions: UserOption[] | undefined = useMemo(
    () =>
      props.userEmailProps.value !== undefined
        ? props.userEmailProps.value
            .filter((value) => emailMap[value] !== undefined)
            .map((value) => userEmailOption(emailMap[value]))
        : undefined,
    [props.userEmailProps.value],
  )

  const userEmailSet =
    props.userEmailProps.value !== undefined
      ? new Set(props.userEmailProps.value)
      : new Set<string>()

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    if (props.suggestedProps && props.suggestedProps.showSuggested) {
      if (props.suggestedProps.showOwnUserInSuggested) {
        dispatch(getOwnUser())
      }
      if (props.filterUserbyAssocationTypes) {
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardFrom,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardFrom))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.delegateCreditCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.delegateCreditCardTo))
        }
      } else {
        dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        dispatch(getRecentUsers(UserAssociation.invitationTo))
      }
    }
  }, [])

  const selectedEmails = useMemo(
    () => selectedOptions?.map((selectedOption) => selectedOption.value),
    [selectedOptions],
  )
  const selectedUsers = useMemo(
    () =>
      Object.values(emailMap).filter((user) =>
        selectedEmails?.includes(user.email),
      ),
    [selectedEmails, emailMap],
  )
  const newEmails = useMemo(
    () =>
      Array.from(userEmailSet)?.filter(
        (email) => Object.keys(emailMap).indexOf(email) < 0,
      ),
    [userEmailSet, emailMap],
  )
  const selectedUsersNotInOrg = useMemo(
    () =>
      selectedUsers
        ?.filter(
          (selectedUser) =>
            selectedUser?.organizationId !== user?.organizationId,
        )
        .map((user) => user.email)
        .concat(newEmails),
    [selectedUsers, user],
  )

  useEffect(() => {
    if (props.setSelectedUsersNotInOrg)
      props?.setSelectedUsersNotInOrg(selectedUsersNotInOrg)
  }, [selectedUsersNotInOrg])

  const handleEmailSelection = (val: string): void => {
    const newSet = new Set(props.userEmailProps.value)
    addDeleteVal(newSet, val)
    props.isMulti
      ? props.userEmailProps.onChange(Array.from(newSet))
      : props.userEmailProps.onChange([val])
  }

  const handleMultiEmailSelection = (val: string[]): void => {
    const newSet = new Set(props.userEmailProps.value)
    val.map((val) => {
      addDeleteVal(newSet, val)
    })
    props.userEmailProps.onChange(Array.from(newSet))
  }

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          position: relative;
        `}
      >
        <UserDropdown
          data-testid={props['data-testid']}
          css={css`
            &:nth-child(n + 2) {
              margin-top: 24px;
            }
            ${getFontCss(
              16,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
            z-index: 3;
          `}
          fieldLabel={props.userFieldLabel}
          selectedOptions={selectedOptions}
          optionGroups={
            props.userOptions.length > 0
              ? [
                  {
                    label:
                      user?.hasOrgAdminPermissions ||
                      user?.organizationRole === OrganizationRoles.cardManager
                        ? translate('${appName} Contacts', {
                            appName: <AppName />,
                          })
                        : translate('${appName} Org Members', {
                            appName: <AppName />,
                          }),
                    options: props.userOptions,
                  },
                ]
              : []
          }
          recentOptionGroups={
            props.recentUserOptions && props.recentUserOptions.length > 0
              ? [
                  {
                    label: translate('Suggested', {
                      appName: <AppName />,
                    }),
                    options: props.recentUserOptions,
                    hideGroup: true,
                  },
                ]
              : []
          }
          isCreatable={!props.unableToAddNewUser}
          isMulti={!!props.isMulti}
          showSuggested={!!props.suggestedProps?.showSuggested}
          isStatic={props.userEmailProps.isStatic}
          errors={props.userEmailProps.errors}
          chooseOption={handleEmailSelection}
          chooseMultiOption={handleMultiEmailSelection}
          selectedValues={userEmailSet}
          placeholder={
            props.isMulti
              ? translateSingleString(locale, 'Enter one or more emails')
              : translateSingleString(locale, 'Type or select from list')
          }
          newOptionLabel={translate('New Contact')}
          infiniteScrollProps={props.userInfiniteScrollProps}
          tooltip={props.tooltip}
          displayPills={props.displayPills}
          id={props.id}
          errorBorder={props.errorBorder}
        />
        {props.getRecipientWarningMessage &&
          props.getRecipientWarningMessage(selectedUsersNotInOrg.length > 0)}
      </div>
      {props.recipientFirstNameProps !== undefined &&
        props.recipientLastNameProps !== undefined &&
        props.selectedUserHasName && (
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              padding: 10px 20px;
            `}
          >
            {translate(
              'This card will use their existing first and last name.',
            )}
          </span>
        )}
      {props.recipientFirstNameProps !== undefined &&
        !props.selectedUserHasName && (
          <StyledTextInputField
            fieldLabel={translate('Recipient First Name')}
            design={TextInputFieldDesigns.New}
            errors={props.recipientFirstNameProps.errors}
            placeholder={translateSingleString(locale, 'Enter first name')}
            value={props.recipientFirstNameProps.value}
            onChange={props.recipientFirstNameProps.onChange}
          />
        )}
      {props.recipientLastNameProps !== undefined &&
        !props.selectedUserHasName && (
          <StyledTextInputField
            fieldLabel={translate('Recipient Last Name')}
            design={TextInputFieldDesigns.New}
            errors={props.recipientLastNameProps.errors}
            placeholder={translateSingleString(locale, 'Enter last name')}
            value={props.recipientLastNameProps.value}
            onChange={props.recipientLastNameProps.onChange}
          />
        )}
      {props.displayNameFieldLabel && props.displayNameProps && (
        <StyledTextInputField
          fieldLabel={props.displayNameFieldLabel}
          design={TextInputFieldDesigns.New}
          placeholder={translateSingleString(locale, 'Type here')}
          showUpdates={props.displayNameProps.showUpdates}
          baseValue={props.displayNameProps.baseValue}
          value={props.displayNameProps.value}
          errors={props.displayNameProps.errors}
          onChange={props.displayNameProps.onChange}
          id={
            getReactNodeSourceString(props.displayNameFieldLabel)
              ?.split(' ')
              .join('') ?? ''
          }
        />
      )}
      {props?.bottomElement}
      {props.showRoles && (
        <SelectRoleOnInvite
          setRole={props.setRole}
          setBookkeeperAccess={props.setBookkeeperAccess}
        />
      )}
    </Box>
  )
}

export default UserAndNameInputBox
